import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import React from "react";
import { ReactComponent as EditIcon } from "../../../common/icons/editGray.svg";
import { VisuallyHiddenInput } from "../styles";
import { Controller, useFormContext } from "react-hook-form";
import FormGrid from "./FormGrid";
import DeleteButton from "../../../common/components/functional/formTable/DeleteButton";

const imageExtensions = ["jpg", "png"];

function formatBytes(bytes: number) {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let index = 0;

  while (bytes >= 1024 && index < units.length - 1) {
    bytes /= 1024;
    index++;
  }

  return `${bytes.toFixed(2)} ${units[index]}`;
}

const readImageFile = (file: File, onLoaded: (dataURL: string) => void) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    onLoaded(reader.result as string);
  };
  reader.readAsDataURL(file);
};

const ImageUploadRow = (props: { isEditing: boolean }) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();

  const allowedImageExtensions: string = imageExtensions
    .map((ext) => `.${ext}`)
    .join(", ");
  const allowedFileExtensions: string = imageExtensions.join(", ");
  const allowedFileSize = 150 * 1024; // 150 KB
  const formattedAllowedFileSize = formatBytes(allowedFileSize);

  const allowedFileTypesText = t("jobAds:imageUploadFormatGuideText", {
    formats: allowedFileExtensions,
  });

  const allowedFileSizeText = t("jobAds:imageUploadFileSizeGuideText", {
    size: formattedAllowedFileSize,
  });

  const tooltipText = `${allowedFileTypesText}\n\n${allowedFileSizeText}`;

  const handleFormChange = (dataUrl: string) => {
    setValue("logo", dataUrl, { shouldDirty: true });
  };

  const handleEdit = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      if (file && file.size > allowedFileSize) {
        alert(
          `${t("jobAds:imageUploadFileSizeTooBig")} ${allowedFileSizeText}`,
        );
        return;
      }
      readImageFile(file, (dataURL) => {
        handleFormChange(dataURL);
      });
    }
  };

  const handleDelete = () => {
    handleFormChange("");
  };

  return (
    <FormGrid title={"Logo"} contentSize={4} infoText={tooltipText}>
      <Controller
        name="logo"
        control={control}
        render={({ field }) => (
          <>
            <Box>
              {field.value ? (
                <Box display="flex" alignItems="center">
                  <img src={field.value} alt="img" height="190px" />
                  {props.isEditing && (
                    <>
                      <Button
                        component="label"
                        variant="text"
                        startIcon={<EditIcon />}
                        sx={{
                          margin: "0 1rem",
                          minWidth: "auto",
                          borderRadius: "18px",
                        }}
                      >
                        <Typography color="textSecondary" fontSize="14px">
                          {t("common:edit")}
                        </Typography>
                        <VisuallyHiddenInput
                          disabled={!props.isEditing}
                          type="file"
                          accept={allowedImageExtensions}
                          onChange={handleEdit}
                        />
                      </Button>
                      <DeleteButton
                        id="delete-image" // id only so we render the button
                        handleDelete={(_) => handleDelete()}
                      />
                    </>
                  )}
                </Box>
              ) : props.isEditing ? (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: "18px",
                  }}
                >
                  {t("jobAds:uploadImage")}
                  <VisuallyHiddenInput
                    type="file"
                    accept={allowedImageExtensions}
                    onChange={handleEdit}
                  />
                </Button>
              ) : (
                <Typography>-</Typography>
              )}
            </Box>
          </>
        )}
      />
    </FormGrid>
  );
};

export default ImageUploadRow;
